import {
  type ActionButtonColor,
  type ActionButtonType,
} from '@myre/ui-primitive-components';
import { type Upload } from '@myre/ui-primitive-core';

import { type UntypedFormControl } from '@angular/forms';

import { type Method } from '../components/upload/upload.component';

import { QuestionBase, type IQuestionBase } from './question-base';

export interface IQuestionUpload<T> extends IQuestionBase<T> {
  uploadId?: number;
  accept?: string;
  adaptText?: boolean;
  authorization?: string;
  auto?: boolean;
  avatarSize?: number;
  bodyFormatter?: (files: File) => any;
  clearOnEnd?: boolean;
  color?: ActionButtonColor;
  design?: 'button' | 'area';
  extractData?: (json: any) => T;
  files?: (File | string)[];
  getPreviewUrl?: (a: T) => string;
  handleResponse?: (a: any) => any;
  iconName?: string;
  includeContentTypeHeader?: boolean;
  keepUploadingOnDestroy?: boolean;
  loading?: boolean;
  method?: Method;
  name?: string;
  onBeforeSend?: (files: File) => void;
  onError?: (err: any) => void;
  onSelect?: (files: File) => void;
  onUpload?: () => void;
  onUploadStart?: (upload: Upload) => void;
  preview?: boolean;
  reverse?: boolean;
  type?: ActionButtonType;
  url: string | ((files: File) => Promise<string>);
  withAvatar?: boolean;
}

export class QuestionUpload<T = string>
  extends QuestionBase<T>
  implements IQuestionUpload<T>
{
  public readonly uploadId?: number;
  public accept: string;
  public adaptText: boolean = true;
  public authorization?: string;
  public auto: boolean = true;
  public avatarSize: number = 100;
  public bodyFormatter: (files: File) => any;
  public clearOnEnd: boolean;
  public color: ActionButtonColor = 'blue';
  public design: 'button' | 'area';
  public extractData: (json: any) => T;
  public getPreviewUrl: (a: T) => string;
  public handleResponse: (rep: any) => any;
  public iconName: string;
  public includeContentTypeHeader: boolean = false;
  public keepUploadingOnDestroy: boolean;
  public loading: boolean = false;
  public method: Method;
  public name: string;
  public onBeforeSend: (files: File) => void;
  public onError: (err: any) => void;
  public onSelect: (files: File) => void;
  public onUpload?: () => void;
  public onUploadStart?: (upload: Upload) => void;
  public preview: boolean;
  public reverse: boolean = true;
  public override type: ActionButtonType = 'primary';
  public url: string | ((files: File) => Promise<string>);
  public withAvatar: boolean = false;

  declare protected _formControl: UntypedFormControl;

  private _upload: () => Promise<void>;

  constructor(options: IQuestionUpload<T>) {
    super(options);
    this.elementType = 'upload';
    this.uploadId = options.uploadId;
    this.accept = options.accept || '';
    this.url = options.url;
    this.keepUploadingOnDestroy = options.keepUploadingOnDestroy || false;
    this.loading = options.loading || false;
    this.name = options.name;
    this.preview = options.preview !== false;
    this.onBeforeSend = options.onBeforeSend || (() => {});
    this.onUploadStart = (event: Upload) => {
      (this as any).uploadId = event.uploadId;

      if (options.onUploadStart) {
        options.onUploadStart(event);
      }
    };
    this.onError =
      options.onError ||
      (err => {
        const str = err?.message || err?.error?.message || err;

        throw new Error(`Unhandled upload error: ${str}`);
      });
    this.onSelect = options.onSelect || (() => {});
    this.onUpload = options.onUpload || (() => {});
    this.handleResponse = options.handleResponse || (a => a);
    this.getPreviewUrl = options.getPreviewUrl || (a => a as unknown as string);
    this.authorization = options.authorization;
    this.bodyFormatter = options.bodyFormatter;
    this.auto = options.auto !== false;
    this.includeContentTypeHeader = options.includeContentTypeHeader;
    this.method = options.method || 'POST';
    this.extractData = options.extractData || (a => a);
    this.design = options.design || 'area';
    this.label = options.label || '_library.import.file';
    this.iconName = options.iconName;
    this.type = options.type || 'primary';
    this.color = options.color || 'blue';
    this.clearOnEnd = options.clearOnEnd;
    this.adaptText = options.adaptText !== false;
    this.reverse = options.reverse !== false;
    this.withAvatar = !!options.withAvatar;
    this.avatarSize = options.avatarSize || 100;
  }

  public override initEnd(): void {
    this._formControl.registerOnChange((value: string[]) => {
      const errors = this.errors;

      if (errors) {
        delete errors.required;
      }

      if (this.required && value.length === 0) {
        this.setErrors({ required: true });
      }
    });
  }

  public uploaded(ret: { file: File; body: any }): void {
    const rep = this.handleResponse(ret.body);
    const data = this.extractData(rep);

    this.setValue(data);
  }

  public override equals(value: T): boolean {
    if (!value) {
      return false;
    }

    const formValue = this.getValue();

    return this.getPreviewUrl(formValue) === this.getPreviewUrl(value);
  }

  public upload(options: { awaitUpload: true }): Promise<void>;
  public upload(options: { awaitUpload: false }): void;
  public upload(options?: { awaitUpload: boolean }): void;
  public upload(options?: { awaitUpload?: boolean }): void | Promise<void> {
    const promise =
      typeof this._upload === 'function' ? this._upload() : Promise.resolve();

    return options?.awaitUpload ? promise : undefined;
  }

  public registerUpload(fnc: () => Promise<void>): void {
    this._upload = fnc;
  }
}
